/**
 * Hearing a call to an AI persona while it happens — the pure half (LiveListen.jsx is the rest).
 *
 * Brian, 2026-10-26: the demo is in a room, and "the person listening or seeing on the dashboard"
 * has to understand what their own calls will be like. So pressing Call on an AI persona (a demo
 * driver or applicant on a 555-01xx number, backend/src/persona.mjs) rings in the browser, then
 * plays the live call through the laptop: Alfred and the AI, as the platform runs it.
 *
 * ⛔ PERSONA CALLS ONLY. The listen pass is minted by the platform for a persona call and for
 * nothing else; a real driver has not agreed to be listened to from this dashboard.
 *
 * ⛔ THE RING IS THE BROWSER'S. The voice box answers a persona's line at once and plays no
 * ringback (PANEL_INBOUND_RINGBACK=0 on the box), so without this the room would hear silence
 * between Call and "Yeah, this is Mike." It rings until somebody on the call speaks.
 */

// US ringback: 440 + 480 Hz, two seconds on, four off. Quiet: it plays through the room's speakers.
export const RING = Object.freeze({ freqs: [440, 480], onMs: 2000, offMs: 4000, gain: 0.06 });

// Ask for the listen pass every second until the platform has the call's room (box-first stamps
// it a second or two after the dial); give up after this long, which is a fault, not slowness.
export const LISTEN_RETRY_MS = 1000;
export const LISTEN_WAIT_MS = 45_000;
// A ring nobody answers stops on its own: a persona answers within seconds or not at all.
export const RING_MAX_MS = 60_000;

/**
 * What a refusal of the listen pass means: ask again, the call is over, or it will not work.
 * A blip is asked again until the deadline (found in review): no answer at all (status 0 or none),
 * the shared key's 429, and a 5xx gateway or outage. A 404 — not a persona call — never is.
 */
export function listenRefusal(err) {
  const code = err?.body?.error;
  const st = err?.status;
  if (st === 409 && code === 'no_room') return 'retry';
  if (st === 409 && code === 'call_ended') return 'ended';
  if (!st || st === 429 || st === 502 || st === 503 || st === 504) return 'retry';
  return 'fail';
}

/**
 * Who a room participant is to the people watching. The room holds Alfred (an agent), the phone
 * leg that carries the persona (the first phone leg, there from the start), and — after Alfred puts
 * the AI applicant through — a second phone leg: the recruiter. `kinds` is livekit-client's
 * ParticipantKind, passed in so this file needs no SDK; the identity prefixes are the fallback
 * (LiveKit names SIP participants `sip_…`, agents `agent-…`).
 */
export function roleOf(p, farIdentity, kinds = {}) {
  if (!p) return 'other';
  const id = String(p.identity || '');
  // A recorder is not on the call.
  if (kinds.EGRESS !== undefined && p.kind === kinds.EGRESS) return 'egress';
  // ⛔ THE WARM-TRANSFER CONSULT IS ALFRED TOO (found in review). The panel joins it as
  // `consult-agent` with a plain token, so it is neither an agent kind nor `agent-…`; without this
  // the room saw "Recruiter" light up while Alfred asked the recruiter "can you take the call?".
  const isAgent = p.isAgent === true || (kinds.AGENT !== undefined && p.kind === kinds.AGENT) || /^(agent|consult)[-_]/i.test(id);
  if (isAgent) return 'alfred';
  const isSip = (kinds.SIP !== undefined && p.kind === kinds.SIP) || /^sip[-_]/i.test(id);
  if (isSip) return !farIdentity || id === farIdentity ? 'far' : 'other';
  return 'other';
}

/** The phone leg that carries the persona: the earliest SIP participant in the room. */
export function farLegOf(participants, kinds = {}) {
  const sip = (participants || []).filter((p) => roleOf(p, null, kinds) === 'far');
  sip.sort((a, b) => (Number(a.joinedAt) || 0) - (Number(b.joinedAt) || 0));
  return sip[0]?.identity || null;
}

export const firstName = (name) => String(name || '').trim().split(/\s+/)[0] || 'them';

export function labelFor(role, name) {
  if (role === 'alfred') return 'Alfred';
  if (role === 'far') return firstName(name);
  return 'Recruiter';
}

/** The line at the top of the panel, for each state. */
export function headline(state, name) {
  const f = firstName(name);
  switch (state) {
    case 'connecting':
    case 'ringing': return `Ringing ${f}…`;
    case 'live': return `On the line with ${f}`;
    case 'ended': return 'The call has ended';
    case 'blocked': return 'The browser is holding the sound';
    default: return 'Could not play the call here';
  }
}
